import React, { useEffect, useRef, useState } from "react";
import { ACCENT, SecondaryButton } from "./shared";
import { checkArtworkFile, formatBytes, MESSAGES, normaliseFile, trackApi, trackErrorMessage, uploadToStorage } from "./trackApi";

// Cover artwork: JPG / PNG, size and pixel checks in the browser (the API checks again), preview,
// replace and remove.
export default function ArtworkUploader({ track, rules, disabled, onTrackChange, ensureTrack }) {
  const [state, setState] = useState({ busy: false, progress: 0, error: "", file: null });
  const [previewUrl, setPreviewUrl] = useState("");
  const [removing, setRemoving] = useState(false);
  const inputRef = useRef(null);
  const controller = useRef(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      if (controller.current) controller.current.abort();
    };
  }, []);

  useEffect(() => {
    if (!state.file || !window.URL?.createObjectURL) {
      setPreviewUrl("");
      return undefined;
    }
    const objectUrl = URL.createObjectURL(state.file);
    setPreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [state.file]);

  const patch = (next) => mounted.current && setState((s) => ({ ...s, ...next }));

  const start = async (rawFile) => {
    if (!rawFile || state.busy || disabled) return;
    const file = normaliseFile(rawFile);
    const checked = await checkArtworkFile(file, rules);
    if (checked.error) {
      patch({ error: checked.error, file: null });
      return;
    }
    patch({ busy: true, progress: 0, error: "", file });
    const target = track || (ensureTrack ? await ensureTrack() : null);
    if (!target) {
      patch({ busy: false, file: null });
      return;
    }
    const ctrl = new AbortController();
    controller.current = ctrl;
    try {
      const stored = await uploadToStorage(file, { signal: ctrl.signal, onProgress: (p) => patch({ progress: p }) });
      const updated = await trackApi.attachMedia({
        track_id: target.id,
        media_type: "artwork",
        ...stored,
        width: checked.width,
        height: checked.height,
      });
      if (mounted.current) onTrackChange(updated);
      patch({ busy: false, file: null, progress: 0 });
    } catch (e) {
      patch({
        busy: false,
        file: null,
        progress: 0,
        error: e?.code === "ERR_CANCELED" ? "" : e?.response ? trackErrorMessage(e) : e?.message || MESSAGES.uploadFailed,
      });
    }
  };

  const remove = async () => {
    if (removing) return;
    setRemoving(true);
    try {
      onTrackChange(await trackApi.removeMedia(track.id, "artwork"));
    } catch (e) {
      patch({ error: trackErrorMessage(e, MESSAGES[500]) });
    } finally {
      setRemoving(false);
    }
  };

  const shown = previewUrl || track?.artwork?.url;
  const min = `${rules.artwork.min_width} x ${rules.artwork.min_height}`;

  return (
    <div data-testid="artwork-uploader">
      <div className="flex flex-col sm:flex-row gap-4 items-start">
        <div className="w-40 h-40 rounded-2xl bg-gray-100 border border-gray-200 overflow-hidden flex items-center justify-center text-gray-400 text-sm shrink-0">
          {shown ? <img src={shown} alt="Cover artwork preview" className="w-full h-full object-cover" /> : "No artwork"}
        </div>
        <div className="min-w-0">
          <p className="text-sm text-gray-600">
            JPG or PNG, up to {formatBytes(rules.artwork.max_bytes)}, at least {min} pixels
            {rules.artwork.require_square ? ", square" : ""}.
          </p>
          {track?.artwork && !state.busy && (
            <p className="text-sm text-gray-500 mt-1 truncate" data-testid="artwork-name">
              {track.artwork.file_name} · {formatBytes(track.artwork.file_size)}
            </p>
          )}
          {state.busy && (
            <div className="mt-3 w-56">
              <div className="h-2 rounded-full bg-gray-200 overflow-hidden" role="progressbar" aria-valuenow={Math.round(state.progress * 100)} aria-valuemin={0} aria-valuemax={100}>
                <div className="h-full transition-all" style={{ width: `${Math.round(state.progress * 100)}%`, backgroundColor: ACCENT }} />
              </div>
              <div className="flex justify-between mt-1 text-sm text-gray-600">
                <span>Uploading… {Math.round(state.progress * 100)}%</span>
                <button type="button" className="font-semibold text-red-700 underline" onClick={() => controller.current && controller.current.abort()}>
                  Cancel
                </button>
              </div>
            </div>
          )}
          {!state.busy && (
            <div className="flex gap-3 mt-3">
              <SecondaryButton className="!py-1.5 !px-4 text-sm" disabled={disabled} onClick={() => inputRef.current && inputRef.current.click()}>
                {track?.artwork ? "Replace artwork" : "Choose artwork"}
              </SecondaryButton>
              {track?.artwork && !disabled && (
                <button type="button" onClick={remove} disabled={removing} className="text-sm font-semibold text-red-700 underline disabled:opacity-50">
                  {removing ? "Removing…" : "Remove"}
                </button>
              )}
            </div>
          )}
          {state.error && (
            <p className="mt-3 text-sm text-red-700" role="alert">{state.error}</p>
          )}
        </div>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={rules.artwork.extensions.map((x) => `.${x}`).join(",")}
        className="hidden"
        data-testid="artwork-input"
        aria-label="Cover artwork"
        onChange={(e) => {
          const picked = e.target.files?.[0];
          e.target.value = "";
          start(picked);
        }}
      />
    </div>
  );
}
